import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LuCreditCard,
  LuLayoutDashboard,
  LuUsers,
  LuMail,
  LuActivity,
  LuDatabase,
  LuGlobe,
  LuLifeBuoy,
  LuHandshake,
  LuShield,
  LuFileText,
  LuGauge,
  LuLogOut,
  LuExternalLink,
  LuShieldCheck,
  LuHeartPulse,
  LuTrash2,
  LuMessageSquare,
  LuBell,
  LuSend,
  LuSearch,
  LuTrendingUp,
  LuKey,
  LuFlag,
  LuShieldX,
  LuWebhook,
  LuFileCode,
  LuShieldAlert,
  LuGauge as LuGauge2,
} from "react-icons/lu";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const NAV: NavSection[] = [
  {
    label: "Overview",
    items: [
      { to: "/overview", label: "Overview", icon: LuLayoutDashboard },
      { to: "/search", label: "Search", icon: LuSearch },
      { to: "/status", label: "Status", icon: LuHeartPulse },
      { to: "/monitoring", label: "Monitoring", icon: LuActivity },
      { to: "/platform", label: "Platform", icon: LuGauge },
      { to: "/events", label: "Events", icon: LuBell },
      { to: "/notifications", label: "Notifications", icon: LuSend },
    ],
  },
  {
    label: "Product",
    items: [
      { to: "/users", label: "Users", icon: LuUsers },
      { to: "/billing", label: "Billing", icon: LuCreditCard },
      { to: "/top-consumers", label: "Top consumers", icon: LuTrendingUp },
      { to: "/api-keys", label: "API keys", icon: LuKey },
      { to: "/user-webhooks", label: "User webhooks", icon: LuWebhook },
      { to: "/rate-limits", label: "Rate limits", icon: LuGauge2 },
      { to: "/inboxes", label: "Inboxes", icon: LuMail },
      { to: "/messages", label: "Messages", icon: LuMessageSquare },
      { to: "/domains", label: "Domains", icon: LuGlobe },
      { to: "/domain-health", label: "Domain health", icon: LuShieldCheck },
      { to: "/database", label: "Database", icon: LuDatabase },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/support", label: "Support", icon: LuLifeBuoy },
      { to: "/sponsors", label: "Sponsors", icon: LuHandshake },
      { to: "/cleanup", label: "Data cleanup", icon: LuTrash2 },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/operators", label: "Operators", icon: LuShield },
      { to: "/audit", label: "Audit log", icon: LuFileText },
      { to: "/feature-flags", label: "Feature flags", icon: LuFlag },
      { to: "/disposable-domains", label: "Disposable list", icon: LuShieldX },
      { to: "/migrations", label: "Migrations", icon: LuFileCode },
      { to: "/maintenance", label: "Maintenance", icon: LuShieldAlert },
    ],
  },
];

export function ConsoleLayout() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useQuery({
    queryKey: ["console", "auth", "me"],
    queryFn: () => api.auth.me(),
  });
  const openEvents = useQuery({
    queryKey: ["console", "events", "open-count"],
    queryFn: () => api.events.list({ status: "open", limit: 1 }),
    refetchInterval: 60_000,
    select: (d) => d.breakdown.open,
  });

  async function handleLogout() {
    try {
      await api.auth.logout();
    } catch {
      // logout is best-effort - drop the token anyway and redirect
    }
    qc.clear();
    toast.success("Signed out");
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex h-full">
      <aside className="w-64 shrink-0 border-r border-[var(--color-border)] sidebar-gradient flex flex-col">
        {/* Brand */}
        <div className="px-5 py-5 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt=""
              className="w-8 h-8 rounded-md object-cover ring-1 ring-[var(--color-border)]"
            />
            <div className="leading-tight">
              <p className="text-sm font-semibold text-[var(--color-text)]">
                destroy
                <span className="text-[var(--color-accent-light)]">.</span>
                network
              </p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-subtle)] mt-0.5">
                Operator Console
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {NAV.map((section) => (
            <div key={section.label}>
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-subtle)]">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      [
                        "group flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors relative",
                        isActive
                          ? "bg-[rgba(139,92,246,0.12)] text-[var(--color-text)]"
                          : "text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)]",
                      ].join(" ")
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r bg-[var(--color-accent-light)]" />
                        )}
                        <item.icon
                          className={[
                            "w-4 h-4 transition-colors",
                            isActive
                              ? "text-[var(--color-accent-light)]"
                              : "text-[var(--color-text-subtle)] group-hover:text-[var(--color-text-muted)]",
                          ].join(" ")}
                        />
                        <span>{item.label}</span>
                        {item.to === "/events" &&
                          openEvents.data !== undefined &&
                          openEvents.data > 0 && (
                            <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-[rgba(237,66,69,0.15)] border border-[rgba(237,66,69,0.3)] text-[#fca5a5] tabular-nums">
                              {openEvents.data > 99 ? "99+" : openEvents.data}
                            </span>
                          )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer: session + logout + product link */}
        <div className="border-t border-[var(--color-border)] p-4 space-y-3">
          {session.data ? (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--color-accent)] to-[var(--color-glow)] flex items-center justify-center text-white text-xs font-semibold shrink-0">
                {session.data.email.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[var(--color-text)] truncate">
                  {session.data.email}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-subtle)] mt-0.5">
                  {session.data.role}
                </p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="text-[var(--color-text-subtle)] hover:text-[var(--color-text)] transition-colors p-1"
                title="Sign out"
              >
                <LuLogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[var(--color-elevated)] shrink-0 relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:via-white/[0.04] before:to-transparent before:animate-[skeleton-shimmer_1.6s_infinite]" />
              <div className="flex-1 space-y-1.5">
                <div className="h-2.5 w-28 rounded bg-[var(--color-elevated)] relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:via-white/[0.04] before:to-transparent before:animate-[skeleton-shimmer_1.6s_infinite]" />
                <div className="h-2 w-12 rounded bg-[var(--color-elevated)] relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:via-white/[0.04] before:to-transparent before:animate-[skeleton-shimmer_1.6s_infinite]" />
              </div>
            </div>
          )}
          <a
            href="https://destroy.network"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between text-[11px] text-[var(--color-text-subtle)] hover:text-[var(--color-text-muted)] transition-colors px-1"
          >
            <span>destroy.network</span>
            <LuExternalLink className="w-3 h-3" />
          </a>
        </div>
      </aside>
      <main className="flex-1 overflow-auto bg-[var(--color-bg)]">
        <Outlet />
      </main>
    </div>
  );
}
